<template>
  <div
    class="rc-card"
    :class="{ 'rc-card--hovered': hovered }"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <div class="rc-image-area" @click="onSelect" v-loading="isLoading">
      <!-- Imagen real o placeholder -->
      <img
        v-if="imageData"
        :src="imageData"
        class="rc-thumb"
        alt="Imagen de la carretilla"
      />
      <div v-else class="rc-placeholder">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          style="opacity: 0.2"
        >
          <path
            d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"
            stroke="white"
            stroke-width="1.5"
            fill="none"
          />
          <circle
            cx="12"
            cy="13"
            r="4"
            stroke="white"
            stroke-width="1.5"
            fill="none"
          />
        </svg>
        <span class="rc-placeholder__text">Sin imagen</span>
      </div>

      <!-- Nombre sobre la imagen (OSD) -->
      <div class="rc-osd-name">{{ name.name }}</div>

      <!-- Punto de estado (esquina superior derecha) -->
      <div
        class="rc-status-dot"
        :class="status ? 'rc-status-dot--online' : 'rc-status-dot--offline'"
      ></div>
    </div>

    <!-- ── Info bar inferior ── -->
    <div class="rc-infobar">
      <div class="rc-infobar__top">
        <div class="rc-infobar__left">
          <div class="rc-infobar__name">{{ name.name }}</div>
          <div class="rc-infobar__timestamp">
            {{
              formattedTimestamp
                ? `Imagen del: ${formattedTimestamp}`
                : "Sin imagen reciente"
            }}
          </div>
        </div>
        <div class="rc-infobar__status">
          <span
            class="rc-infobar__dot"
            :class="
              status ? 'rc-infobar__dot--online' : 'rc-infobar__dot--offline'
            "
          ></span>
          <span
            class="rc-infobar__status-text"
            :class="
              status
                ? 'rc-infobar__status-text--online'
                : 'rc-infobar__status-text--offline'
            "
          >
            {{ status ? "En curso" : "Sin orden" }}
          </span>
        </div>
      </div>
      <div class="rc-infobar__bottom">
        <div class="rc-infobar__actions">
          <button
            class="rc-infobar-btn rc-infobar-btn--view"
            title="Ver imagen"
            @click.stop="onSelect"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
              />
              <circle
                cx="12"
                cy="12"
                r="3"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
              />
            </svg>
            Ver
          </button>
          <button
            class="rc-infobar-btn rc-infobar-btn--reload"
            title="Recargar imagen"
            @click.stop="reload"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <polyline
                points="1,4 1,10 7,10"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                fill="none"
              />
              <path
                d="M3.51 15a9 9 0 1 0 .49-4.95"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                fill="none"
              />
            </svg>
            Recargar
          </button>
          <button
            v-if="isAdmin && name.isAxis"
            class="rc-infobar-btn rc-infobar-btn--restart"
            title="Reiniciar cámara"
            :disabled="isRestarting"
            @click.stop="onRestartClick"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M23 4v6h-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
              <path
                d="M1 20v-6h6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
              <path
                d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
            </svg>
            {{ isRestarting ? "Reiniciando..." : "Reiniciar" }}
          </button>
          <button
            v-if="isAdmin"
            class="rc-infobar-btn rc-infobar-btn--settings"
            title="Gestionar cámara"
            @click.stop="onSettingsClick"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="3"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
              />
              <path
                d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
                stroke="currentColor"
                stroke-width="2"
                fill="none"
              />
            </svg>
            Ajustes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadLastFrame, loadLastFrameFile } from "@/services/supervisor.api";
import { restartAxisCamera } from "@/services/axis.api";

export default {
  name: "RecorderImage",
  props: {
    name: { type: Object, required: true },
    status: { type: Boolean, required: true },
  },
  data() {
    return {
      imageData: "",
      timestamp: null,
      isLoading: false,
      hovered: false,
      isRestarting: false,
    };
  },
  async created() {
    this.isLoading = true;
    try {
      const { imageData, timestamp } = await loadLastFrameFile(
        this.name.identifier
      );
      this.imageData = imageData;
      this.timestamp = new Date(timestamp);
      this.$emit("heartbeat", {
        identifier: this.name.identifier,
        timestamp: this.timestamp,
      });
    } catch (e) {
      // this.$message.error(
      //   `Error cargando imagen de ${this.name}: ${e.message}`
      // );
    } finally {
      this.isLoading = false;
    }
  },
  computed: {
    formattedTimestamp() {
      if (!this.timestamp) return "";
      return this.timestamp.toLocaleString("es-ES", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    },
    isAdmin() {
      return sessionStorage.getItem("role") === "admin";
    },
  },
  methods: {
    onSelect() {
      this.$emit("select", this.imageData);
    },
    onSettingsClick() {
      this.$emit("settings", this.name);
    },
    async reload() {
      this.isLoading = true;
      try {
        const { imageData, timestamp } = await loadLastFrame(
          this.name.identifier
        );
        this.imageData = imageData;
        this.timestamp = new Date(timestamp);
      } catch (e) {
        this.$emit("load-error", this.name.identifier);
      } finally {
        this.isLoading = false;
      }
    },
    async onRestartClick() {
      try {
        await this.$confirm(
          `¿Estás seguro de que quieres reiniciar la cámara ${this.name.identifier}?`,
          "Confirmar reinicio",
          {
            confirmButtonText: "Reiniciar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
      } catch (e) {
        return;
      }
      this.isRestarting = true;
      try {
        await restartAxisCamera(this.name.identifier);
        this.$message.success(
          `Cámara ${this.name.identifier} reiniciada correctamente`
        );
      } catch (e) {
        if (e.response && e.response.status === 404) {
          this.$message.error("No existe una cámara Axis con ese identifier");
        } else {
          this.$message.error("Error al reiniciar la cámara");
        }
      } finally {
        this.isRestarting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Card ─────────────────────────────────────────────────────── */
.rc-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07);
  transition: border-color 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.rc-card--hovered {
  border-color: #0284c7;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* ── Image area ───────────────────────────────────────────────── */
.rc-image-area {
  position: relative;
  height: 190px;
  background: linear-gradient(160deg, #111827, #1f2937);
  overflow: hidden;
}

.rc-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}
.rc-card--hovered .rc-thumb {
  transform: scale(1.03);
}

/* ── Placeholder ──────────────────────────────────────────────── */
.rc-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  /* Subtle grid pattern */
  background-image: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 30px,
      rgba(255, 255, 255, 0.025) 30px,
      rgba(255, 255, 255, 0.025) 31px
    ),
    repeating-linear-gradient(
      90deg,
      transparent,
      transparent 30px,
      rgba(255, 255, 255, 0.025) 30px,
      rgba(255, 255, 255, 0.025) 31px
    );
}

.rc-placeholder__text {
  color: rgba(255, 255, 255, 0.25);
  font-size: 13px;
  font-weight: 500;
}

/* ── OSD name (bottom-left) ───────────────────────────────────── */
.rc-osd-name {
  position: absolute;
  bottom: 10px;
  left: 12px;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
  letter-spacing: 0.02em;
  pointer-events: none;
}

/* ── Status dot (top-right) ───────────────────────────────────── */
.rc-status-dot {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  pointer-events: none;
}
.rc-status-dot--online {
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.3);
  animation: rcPulseDot 2s ease infinite;
}
.rc-status-dot--offline {
  background: #9ca3af;
}

@keyframes rcPulseDot {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* ── Info bar ─────────────────────────────────────────────────── */
.rc-infobar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid #f1f5f9;
}

.rc-infobar__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.rc-infobar__left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.rc-infobar__name {
  color: #0f172a;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rc-infobar__timestamp {
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rc-infobar__bottom {
  display: flex;
  align-items: center;
}

.rc-infobar__status {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding-top: 2px;
}

.rc-infobar__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.rc-infobar__dot--online {
  background: #22c55e;
  animation: rcPulseDot 2s ease infinite;
}
.rc-infobar__dot--offline {
  background: #9ca3af;
}

.rc-infobar__status-text {
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.rc-infobar__status-text--online {
  color: #16a34a;
}
.rc-infobar__status-text--offline {
  color: #6b7280;
}

/* ── Infobar action buttons (siempre visibles) ────────────────── */
.rc-infobar__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;
}

.rc-infobar-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  font-family: inherit;
  transition: opacity 0.15s;
  white-space: nowrap;
}
.rc-infobar-btn:hover {
  opacity: 0.8;
}
.rc-infobar-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.rc-infobar-btn--view {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #0284c7;
}

.rc-infobar-btn--settings {
  border-color: #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.rc-image-area .el-loading-mask {
  border-radius: 0;
}

.rc-infobar-btn--reload {
  border-color: #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.rc-infobar-btn--restart {
  border-color: #fed7aa;
  background: #fff7ed;
  color: #ea580c;
}
</style>
